import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import {
  createCommunity,
  getCommunity,
  listCommunities,
  listDailyJoins,
  updateCommunity,
  type CreateCommunityInput,
} from '../api/communities';
import type { TablesUpdate } from '@/types';

export function useCommunities(limit?: number) {
  return useQuery({
    queryKey: queryKeys.communities.list({ limit }),
    queryFn: () => listCommunities(limit),
    staleTime: 2 * 60_000,
  });
}

/** Real daily-join counts (trending) for a set of communities, keyed by id. */
export function useDailyJoins(communityIds: string[]) {
  return useQuery({
    queryKey: ['communities', 'daily-joins', communityIds.length, communityIds[communityIds.length - 1] ?? ''],
    queryFn: () => listDailyJoins(communityIds),
    enabled: communityIds.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useCommunity(id: string) {
  return useQuery({
    queryKey: queryKeys.communities.detail(id),
    queryFn: () => getCommunity(id),
    enabled: Boolean(id),
  });
}

/**
 * Create a community. On success we invalidate every `communities` query so the
 * Explore directory and the owner's settings list pick up the new row without a
 * manual refetch.
 */
export function useCreateCommunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommunityInput) => createCommunity(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.communities.all });
    },
  });
}

export function useUpdateCommunity(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TablesUpdate<'communities'>) => updateCommunity(id, input),
    onSuccess: (community) => {
      qc.setQueryData(queryKeys.communities.detail(id), community);
      void qc.invalidateQueries({ queryKey: queryKeys.communities.all });
    },
  });
}
